
import { StyleValue } from '@/shared/app/model/StyleValue';
import Base from '@/shared/app/ui/Base.vue';
import Dataset from '@/shared/data/model/Dataset';
import { isEqual } from 'lodash';
import { Component, Watch } from 'vue-property-decorator';
import {
  CommunicationsFilteringMode,
  DatasetCommunication,
  SiteCommunicationsDataset,
} from '../types';
import {
  communicationsDatasetAlias,
  filterActiveCommunications,
  filterCommunicationsByZones,
  filterSiteDisabledCommunications,
  getCommunicationType,
  parseCommunicationsDataset,
  ParsedCommunicationsDataset,
  sortCommunicationsByName,
} from './utils';

const MINIMUM_COMMUNICATION_DURATION_MS = 1500;

@Component
export default class Communications extends Base {
  additionalDatasetsAliases: string[] = [];
  index = 0;
  src = {
    image: '',
    video: '',
  };
  videoElement: HTMLVideoElement | null = null;
  nextTimeout = 0;
  videoMaskingTimeout = 0;
  showMask = false;
  listeningToVideoEvents = false;

  get playMode(): 'loop' | 'once' {
    return this.configuration.playMode === 'once' ? 'once' : 'loop';
  }

  onCommunicationEnded() {
    if (this.playMode === 'once') {
      this.emitDone();
      return;
    }
    this.next();
  }

  async next() {
    clearTimeout(this.nextTimeout);
    const communications = this.getCommunications();

    const numberOfCommunications = communications.length;
    if (numberOfCommunications === 0) {
      this.noCommunicationsToShow();
      return;
    }
    this.index += 1;
    if (this.index >= numberOfCommunications) this.index = 0;

    const nextCommunication = communications[this.index];
    const nextFilename = this.getFilenameDependingOnOrientation(nextCommunication);
    const nextIsVideo = getCommunicationType(nextCommunication) === 'video' && nextFilename;
    const nextDuration = (nextCommunication.duration || 0) * 1000;

    if (nextFilename && nextIsVideo) {
      this.playAVideo(nextFilename);
    } else if (nextFilename) {
      this.showImage(nextFilename);
      clearTimeout(this.nextTimeout);
      setTimeout(
        () => this.onCommunicationEnded(),
        Math.max(nextDuration, MINIMUM_COMMUNICATION_DURATION_MS),
      );
    } else this.noCommunicationsToShow();
  }

  playAVideo(filename: string) {
    const videoUrl = `${this.configuration.uploadsBaseUrl}${filename}`;
    this.listeningToVideoEvents = true;

    if (this.src.video === videoUrl) {
      this.videoElement!.play();
      return;
    }

    const videoElement = this.videoElement!;
    this.src.image = '';
    this.showMask = true;
    this.src.video = videoUrl;
    videoElement.load();
    clearTimeout(this.videoMaskingTimeout);
    this.videoMaskingTimeout = setTimeout(() => {
      videoElement.play();
      setTimeout(() => {
        this.showMask = false;
      }, 0.1 * MINIMUM_COMMUNICATION_DURATION_MS);
    }, 0.33 * MINIMUM_COMMUNICATION_DURATION_MS);
  }

  showImage(filename: string) {
    this.src.image = `${this.configuration.uploadsBaseUrl}${filename}`;
    this.src.video = '';
    this.showMask = false;
  }

  videoEnded() {
    if (this.listeningToVideoEvents) {
      this.listeningToVideoEvents = false;
      this.onCommunicationEnded();
    }
  }

  videoError() {
    if (this.listeningToVideoEvents) {
      this.listeningToVideoEvents = false;
      clearTimeout(this.nextTimeout);
      setTimeout(() => this.onCommunicationEnded(), 500);
    }
  }

  getFilenameDependingOnOrientation(communication: DatasetCommunication) {
    const element = this.$refs.communications as HTMLElement;
    if (element) {
      const orientation = element.clientWidth > element.clientHeight ? 'horizontal' : 'vertical';
      return communication[`${orientation}Filename`];
    }
    return communication.horizontalFilename;
  }

  noCommunicationsToShow() {
    this.src.image = '';
    this.src.video = '';
    this.showMask = true;
    clearTimeout(this.nextTimeout);
    this.nextTimeout = setTimeout(() => this.next(), 2000);
  }

  get maskStyle(): StyleValue {
    return {
      backgroundColor: this.configuration.mask.backgroundColor,
      backgroundImage: this.configuration.mask.backgroundImage
        ? `url("${this.configuration.mask.backgroundImage}")`
        : undefined,
      opacity: this.configuration.mask.opacity,
      backgroundSize: this.configuration.mask.backgroundSize,
      backgroundPosition: this.configuration.mask.backgroundPosition,
    };
  }

  getCommunications(): DatasetCommunication[] {
    let { communications } = this.primaryCommunicationsDataset;

    if (this.configuration.isOverriden) return communications;

    this.additionalDatasetsAliases.forEach((listeningToDatasetName) => {
      const rawDataset = this.dataset(listeningToDatasetName);
      const communicationsDataset = parseCommunicationsDataset(rawDataset);
      communications.push(...communicationsDataset.communications);
    });

    communications = filterActiveCommunications(communications);
    communications = filterCommunicationsByZones(
      communications,
      this.configuration.zones,
      this.configuration.filteringMode,
    );
    const siteIdToFilterOn = this.primaryCommunicationsDataset.objectId;
    if (siteIdToFilterOn)
      communications = filterSiteDisabledCommunications(communications, siteIdToFilterOn);

    communications = sortCommunicationsByName(communications);

    return communications;
  }

  @Watch('primaryCommunicationsDataset')
  subscribeToAdditionalDatasets(commDs: ParsedCommunicationsDataset) {
    const newSubscribeTo = commDs.subscribeTo ?? [];
    const newSubscribeToAliases = newSubscribeTo
      .map((subToDatasetName) => communicationsDatasetAlias(subToDatasetName))
      .sort();
    if (!isEqual(newSubscribeToAliases, this.additionalDatasetsAliases)) {
      this.subscribeToDatasets(newSubscribeTo);
      this.additionalDatasetsAliases = newSubscribeToAliases;
    }
  }
  subscribeToDatasets(datasetsNames: string[]) {
    const datasetsNamesWithAliases = datasetsNames.map((datasetName) => {
      return {
        name: datasetName,
        alias: communicationsDatasetAlias(datasetName),
      };
    });

    // eslint-disable-next-line no-console
    console.debug('Subscribing to datasets:', datasetsNames);
    if (this.realtime) this.realtime.subscribeToDatasets(datasetsNamesWithAliases);
  }

  get primaryCommunicationsDataset() {
    if (this.configuration.isOverriden)
      return parseCommunicationsDataset(this.template?.datasets?.overrideCommunications as Dataset);
    const rawDataset: SiteCommunicationsDataset = this.dataset('communications');
    return parseCommunicationsDataset(rawDataset);
  }

  get configuration() {
    let filteringMode = this.config.mode?.toString() as CommunicationsFilteringMode;
    if (!Object.values(CommunicationsFilteringMode).includes(filteringMode))
      filteringMode = CommunicationsFilteringMode.ALL;
    const zones = this.config.myZones?.toString().split(',') ?? [];
    return {
      filteringMode,
      zones,
      mask: {
        backgroundColor: this.config.maskBackgroundColor?.toString() ?? 'black',
        backgroundImage: this.config.maskBackgroundImage?.toString() ?? '',
        opacity: Number(this.config.maskOpacity) || 1,
        backgroundSize: this.config.maskBackgroundSize?.toString() ?? 'cover',
        backgroundPosition: this.config.maskBackgroundPosition?.toString() ?? 'center',
      },
      isOverriden: Object.keys(this.template?.datasets ?? {}).includes('overrideCommunications'),
      uploadsBaseUrl: this.config.uploadsBaseUrl?.toString() ?? '/uploads/',
      playMode: this.config.playMode?.toString() === 'once' ? 'once' : 'loop',
    };
  }

  mounted() {
    this.videoElement = this.$refs.video as HTMLVideoElement;
    this.videoElement.addEventListener('error', this.videoError);
    this.videoElement.addEventListener('ended', this.videoEnded);
    this.$nextTick(() => {
      this.next();
    });
  }

  beforeUnmount() {
    if (this.videoElement) {
      this.videoElement.removeEventListener('error', this.videoError);
      this.videoElement.removeEventListener('ended', this.videoEnded);
    }
    clearTimeout(this.nextTimeout);
    clearTimeout(this.videoMaskingTimeout);
  }
}
